import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { BsCheck, BsCheckAll } from 'react-icons/bs';
import { formatMessageDate, formatTime } from '../F_utils/dateUtils';
import '../styles/Chat.css';

//Chat component
const Chat = ({ advisorId, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);  
  const messagesEndRef = useRef(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0 }); // For positioning

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUserId(response.data.user._id);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchMessages = useCallback(async () => {
    if (!advisorId || !userId) {
      setError('Invalid chat parameters');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to continue.');
        setLoading(false);
        return;
      }

      // Determine which user's chat to fetch
      const chatPartnerId = currentUserId === advisorId ? userId : advisorId;
      
      const response = await axios.get(`/api/auth/chat/${chatPartnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.messages) {
        setMessages(response.data.messages);
        setLoading(false);
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      const errorMessage = error.response?.data?.msg || 'Failed to load messages. Please try again later.';
      setError(errorMessage);
      setMessages([]);
      setLoading(false);
    }
  }, [advisorId, userId, currentUserId]);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      fetchMessages();  
      const interval = setInterval(fetchMessages, 5000); // Poll for new messages every 5 seconds
      
      return () => {
        clearInterval(interval);
      };
    }
  }, [fetchMessages, currentUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to send messages.');
        setSending(false);
        return;
      }

      // Determine recipient based on current user
      const recipientId = currentUserId === advisorId ? userId : advisorId;

      await axios.post('/api/auth/chat/send', {
        recipientId,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage('');
      setError(null);
      // Fetch messages immediately after sending to show the new message
      await fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.msg || 'Failed to send message. Please try again.';
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteForMe = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/auth/chat/message/${messageId}/delete-for-me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchMessages(); // Refresh messages
      setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const handleDeleteForEveryone = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      // Determine chat partner ID based on current user
      const chatPartnerId = currentUserId === advisorId ? userId : advisorId;
      
      await axios.delete(`/api/auth/chat/message/${messageId}/delete-for-everyone`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { chatPartnerId }
      });

      // Update local messages state immediately
      setMessages(prevMessages => 
        prevMessages.map(message => 
          message._id === messageId 
            ? { ...message, deletedForEveryone: true }
            : message
        )
      );

      // Fetch latest messages to ensure sync
      await fetchMessages();
      setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message for everyone:', error);
      setError('Failed to delete message for everyone');
    }
  };

  // Helper to get recipientId for tick logic
  const getRecipientId = () => (currentUserId === advisorId ? userId : advisorId);

  // Hide dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (selectedMessage) setSelectedMessage(null);
    };
    if (selectedMessage) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [selectedMessage]);

  // Long press logic for mobile/touch
  let longPressTimer = null;
  const handleTouchStart = (messageId, e) => {
    longPressTimer = setTimeout(() => {
      setSelectedMessage(messageId);
      setDropdownPos({ x: e.touches?.[0]?.clientX || 0, y: e.touches?.[0]?.clientY || 0 });
    }, 500);
  };
  const handleTouchEnd = () => {
    clearTimeout(longPressTimer);
  };

  // Right click logic for desktop
  const handleContextMenu = (messageId, e) => {
    e.preventDefault();
    setSelectedMessage(messageId);
    setDropdownPos({ x: e.clientX, y: e.clientY });
  };

  if (!advisorId || !userId) {
    return <div className="chat-error">Invalid chat parameters</div>;
  }

  if (loading) return <div className="chat-loading">Loading chat...</div>;
  if (error) return <div className="chat-error">{error}</div>;

  return (
    <div className="chat-container">
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">No messages yet. Start the conversation!</div>
        ) : (
          <>
            {messages.map((message, index) => {
              // Don't show messages that are deleted for everyone or deleted for current user
              if (message.deletedForEveryone || message.deletedFor?.includes(currentUserId)) {
                return null;
              }

              // Check if we need to show a date separator
              const showDateSeparator = index === 0 || 
                new Date(message.timestamp).toDateString() !== 
                new Date(messages[index - 1]?.timestamp).toDateString();

              const isSentByMe = message.sender._id === currentUserId;
              const recipientId = getRecipientId();
              const isDelivered = isSentByMe && message.readBy?.includes(recipientId);

              // Show dropdown if this message is selected
              const showDropdown = selectedMessage === message._id;

              return (
                <React.Fragment key={message._id || Date.now() + Math.random()}>
                  {showDateSeparator && (
                    <div className="message-date-separator">
                      <span>{formatMessageDate(message.timestamp)}</span>
                    </div>
                  )}
                  <div
                    className={`message ${isSentByMe ? 'sent' : 'received'}`}
                    onContextMenu={(e) => handleContextMenu(message._id, e)}
                    onTouchStart={(e) => handleTouchStart(message._id, e)}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="message-content">
                      {message.content}
                      {/* Remove 3-dots button */}
                      {showDropdown && (
                        <div
                          className="message-options-dropdown"
                          style={{
                            position: 'fixed',
                            left: dropdownPos.x,
                            top: dropdownPos.y,
                            zIndex: 2000,
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          <button onClick={() => { handleDeleteForMe(message._id); setSelectedMessage(null); }}>
                            Delete for me
                          </button>
                          {isSentByMe && (
                            <button onClick={() => { handleDeleteForEveryone(message._id); setSelectedMessage(null); }}>
                              Delete for everyone
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="message-time">
                      {formatTime(new Date(message.timestamp))}
                      {isSentByMe && (
                        <span className="tickmark">
                          {isDelivered ? <BsCheckAll color="#0084ff" /> : <BsCheck />}
                        </span>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSendMessage} className="message-input-form">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          className="message-input"
          disabled={sending}
        />
        <button type="submit" className="send-button" disabled={sending || !newMessage.trim()}>
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
      {error && <div className="chat-error message-error">{error}</div>}
    </div>
  );
};

export default Chat;